/**
 * Music Generation API Service
 *
 * Provides methods to interact with the Lyria 3 music generation backend.
 * Supports text-to-music generation, job status polling, and lyrics drafting.
 */

import type { ApiResponse } from '../types';
import type { UnifiedJob } from './jobs';
import { buildApiUrl } from './urlResolver';
import {
  JSON_HEADERS,
  requestWrapped,
  extractApiErrorMessage,
  getAuthHeadersFromStorage,
} from '../utils/httpUtils';

export interface MusicModel {
  id: string;
  display_name: string;
  description: string;
  max_duration_seconds: number;
  cost_usd: number;
  estimated_tokens: number;
  supports_lyrics: boolean;
  supports_vocals: boolean;
  provider: string;
}

export interface MusicGenerateRequest {
  prompt: string;
  model: string;
  format: 'mp3';
}

export interface MusicLyricsRequest {
  prompt: string;
  max_length?: number;
}

export interface MusicLyricsResponse {
  lyrics: string;
}

export interface MusicGenerateData {
  job_id: string;
  model: string;
  estimated_seconds: number;
  estimated_tokens: number;
}

export interface MusicJobStatusData {
  job_id: string;
  status: 'pending' | 'processing' | 'completed' | 'failed' | 'cancelled';
  progress: number;
  message: string;
  audio_url: string | null;
  audio_path: string | null; // Local filesystem path (desktop/local mode only)
  lyrics: string | null;
  model: string;
  duration_seconds: number | null;
  cost_usd: number;
  error: string | null;
  started_at: string;
  completed_at: string | null;
  elapsed_seconds: number;
}

export const musicGenerationAPI = {
  /**
   * Submit a music generation job.
   */
  async generateMusic(request: MusicGenerateRequest): Promise<ApiResponse<MusicGenerateData>> {
    return requestWrapped(
      buildApiUrl('/api/v1/music/generate'),
      {
        method: 'POST',
        headers: await getAuthHeadersFromStorage(),
        body: JSON.stringify({
          prompt: request.prompt,
          model: request.model,
          format: request.format,
        }),
      },
      'Failed to start music generation'
    );
  },

  /**
   * Get the status of a music generation job.
   */
  async getJobStatus(jobId: string): Promise<ApiResponse<MusicJobStatusData>> {
    // ONE polling endpoint (integrationMode Stage 01). The per-capability
    // /music/jobs/{id} route this used to call is deleted: music jobs live in
    // the durable job store registered with the unified job registry on BOTH
    // servers, and every music-specific field it returned at the top level is
    // carried in extra_metadata (the job record's metadata) once the track is
    // done.
    const response = await requestWrapped<UnifiedJob>(
      buildApiUrl(`/api/v1/jobs/${jobId}`),
      { method: 'GET', headers: await getAuthHeadersFromStorage() },
      'Failed to get music job status'
    );
    if (!response.success || !response.data) {
      return response as unknown as ApiResponse<MusicJobStatusData>;
    }

    const job = response.data;
    const extra = (job.extra_metadata ?? {}) as Record<string, unknown>;
    return {
      ...response,
      data: {
        job_id: job.job_id,
        status: job.status as MusicJobStatusData['status'],
        progress: job.progress,
        message: job.message,
        audio_url: job.result_url ?? (extra.audio_url as string | null) ?? null,
        audio_path: (extra.audio_path as string | null) ?? null,
        lyrics: (extra.lyrics as string | null) ?? null,
        model: (extra.model as string) ?? '',
        duration_seconds: (extra.duration_seconds as number | null) ?? null,
        cost_usd: (extra.cost_usd as number) ?? 0,
        error: job.error,
        started_at: job.started_at ?? '',
        completed_at: job.completed_at,
        elapsed_seconds: job.elapsed_seconds ?? 0,
      },
    } as ApiResponse<MusicJobStatusData>;
  },

  /**
   * List available music generation models.
   */
  async listModels(): Promise<ApiResponse<{ models: MusicModel[] }>> {
    return requestWrapped(
      buildApiUrl('/api/v1/music/models'),
      { method: 'GET', headers: await getAuthHeadersFromStorage() },
      'Failed to list music models'
    );
  },

  /**
   * Draft song lyrics via the LLM cascade.
   *
   * The returned lyrics are embedded into the Lyria prompt so that Lyria
   * follows them closely during generation.
   *
   * @param prompt - Song idea or style description (used as user message)
   * @param maxLength - Max word count hint sent to the LLM (default 400)
   * @returns The drafted lyrics string
   */
  async draftLyrics(prompt: string, maxLength = 400): Promise<string> {
    const authHeaders = await getAuthHeadersFromStorage();
    const response = await fetch(buildApiUrl('/api/v1/music/draft-lyrics'), {
      method: 'POST',
      headers: { ...JSON_HEADERS, ...authHeaders },
      body: JSON.stringify({ prompt, max_length: maxLength } satisfies MusicLyricsRequest),
    });

    const data = await response.json();

    if (!response.ok) {
      throw new Error(extractApiErrorMessage(data, 'Failed to draft lyrics'));
    }

    const lyrics: string = (data.data?.lyrics ?? data.lyrics ?? '') as string;
    if (!lyrics.trim()) {
      throw new Error('Server returned empty lyrics');
    }
    return lyrics;
  },
};

export default musicGenerationAPI;
